import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, MapPin, Users, CheckCircle2, AlertCircle, Calendar, Image as ImageIcon, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HRDashboard() {
  const [user, setUser] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      navigate('/hr/login');
      return;
    }
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'hr') {
      navigate('/hr/login');
      return;
    }
    setUser(parsedUser);
    fetchAttendance();
  }, [navigate]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendance(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/hr/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Photo Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 bg-zinc-900 text-white flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold uppercase">{selectedPhoto.employee_name}</h3>
                <p className="text-sm text-zinc-300">
                  {selectedPhoto.attendance_type.replace('_', ' ').toUpperCase()} - {new Date(selectedPhoto.timestamp).toLocaleString()}
                </p>
              </div>
              <button 
                onClick={() => setSelectedPhoto(null)}
                className="p-2 hover:bg-zinc-800 rounded"
                data-testid="close-photo-modal"
              >
                <X size={24} />
              </button>
            </div>
            <img 
              src={selectedPhoto.selfie_data} 
              alt="Attendance selfie"
              className="w-full max-h-[70vh] object-contain"
              data-testid="attendance-photo-modal"
            />
            <div className="p-4 bg-zinc-50 border-t-2 border-zinc-100">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-zinc-600" />
                  <span className="font-medium">{selectedPhoto.location_name}</span>
                </div>
                <div className={`flex items-center gap-2 ${selectedPhoto.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>
                  {selectedPhoto.status === 'approved' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                  <span className="font-bold uppercase">{selectedPhoto.status}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border-b-2 border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <img 
              src="/anmol-logo.jpg" 
              alt="Advanced Anmol Metcomp Pvt Ltd" 
              className="h-12"
            />
            <button onClick={handleLogout} className="text-sm text-zinc-600 hover:text-zinc-900 font-medium" data-testid="hr-logout-button">
              Logout
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-zinc-900" data-testid="hr-dashboard-title">HR Dashboard</h1>
              <p className="text-sm text-zinc-500 font-medium mt-1" data-testid="hr-user-name">{user.name}</p>
            </div>
          </div>

          <div className="flex gap-4 mt-6 flex-wrap">
            <button
              onClick={() => navigate('/hr/dashboard')}
              className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider rounded-sm"
              data-testid="nav-dashboard"
            >
              Attendance
            </button>
            <button
              onClick={() => navigate('/hr/employees')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-employees"
            >
              Employees
            </button>
            <button
              onClick={() => navigate('/hr/locations')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-locations"
            >
              Locations
            </button>
            <button
              onClick={() => navigate('/hr/reports')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-reports"
            >
              Reports
            </button>
            <button
              onClick={() => navigate('/hr/notifications')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-notifications"
            >
              Notifications
            </button>
            <button onClick={() => navigate('/hr/tracking')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-tracking-from-dashboard">Tracking</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-zinc-500">Loading attendance records...</p>
          </div>
        ) : attendance.length === 0 ? (
          <div className="text-center py-12 bg-white border-2 border-zinc-100 rounded-lg">
            <Calendar size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500 font-medium">No attendance records yet</p>
          </div>
        ) : (
          <div className="bg-white border-2 border-zinc-100 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b-2 border-zinc-100">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider">Employee</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider">Type</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider">Date & Time</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider">Location</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider">Photo</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-zinc-600 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {attendance.map((record, idx) => (
                    <tr key={record.id} className="hover:bg-zinc-50 transition-colors" data-testid={`attendance-row-${idx}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center">
                            <Users size={18} className="text-zinc-600" />
                          </div>
                          <div>
                            <p className="font-bold text-zinc-900" data-testid={`employee-name-${idx}`}>{record.employee_name || 'Unknown'}</p>
                            <p className="font-mono text-xs text-zinc-500">ID: {record.employee_id.slice(0, 8)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {record.attendance_type === 'check_in' ? (
                            <div className="flex items-center gap-2 text-blue-700">
                              <div className="w-2 h-2 rounded-full bg-blue-700"></div>
                              <span className="text-sm font-bold uppercase" data-testid={`type-${idx}`}>Check In</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-purple-700">
                              <div className="w-2 h-2 rounded-full bg-purple-700"></div>
                              <span className="text-sm font-bold uppercase" data-testid={`type-${idx}`}>Check Out</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-mono text-sm text-zinc-900" data-testid={`timestamp-${idx}`}>{new Date(record.timestamp).toLocaleString()}</p>
                      </td>
                      <td className="px-6 py-4">
                        {record.location_name ? (
                          <div className="flex items-center gap-2 text-zinc-700">
                            <MapPin size={14} />
                            <span className="text-sm font-medium" data-testid={`location-${idx}`}>{record.location_name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-zinc-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedPhoto(record)}
                          className="flex items-center gap-2 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 rounded text-sm font-bold uppercase tracking-wider transition-colors"
                          data-testid={`view-photo-${idx}`}
                        >
                          <ImageIcon size={16} />
                          View
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        {record.status === 'approved' ? (
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle2 size={18} />
                            <span className="text-sm font-bold uppercase" data-testid={`status-${idx}`}>Approved</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-red-700">
                            <AlertCircle size={18} />
                            <span className="text-sm font-bold uppercase" data-testid={`status-${idx}`}>Rejected</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}